import { useState, useRef, useEffect } from 'react'
import { ButtonCircle, ButtonSlideScrollLeft, ButtonSlideScrollRight } from '@/components/common/buttons'
import { floor, round } from '@/utils/utilCommon'
import styles from './horizontal-scroll-container.module.css'

const LEFT_MOST = 0
const MOVE = 480
const DEFAULT_BUTTON_POSITION_Y = 50

type HorizontalScrollContainerProps = {
  children: React.ReactNode
  hideRightButton?: boolean
  isCircleType?: boolean
  buttonPositionY?: number
  id?: string
}

export const HorizontalScrollContainer = ({
  children,
  hideRightButton = false,
  isCircleType = false,
  buttonPositionY = DEFAULT_BUTTON_POSITION_Y,
  id,
}: HorizontalScrollContainerProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null)

  const [isRightButtonVisible, setIsRightButtonVisible] = useState<boolean>(true)
  const [isLeftButtonVisible, setIsLeftButtonVisible] = useState<boolean>(false)

  // id가 바뀌면 scroll x를 가장 왼쪽으로 이동 (예를들어 Tab-id)
  useEffect(() => {
    scrollTo(LEFT_MOST)
  }, [id])

  const displayButtons = () => {
    if (!containerRef.current) return

    const { scrollLeft, scrollWidth } = containerRef.current
    const { width } = containerRef.current.getBoundingClientRect()

    setIsLeftButtonVisible(scrollLeft !== LEFT_MOST)
    setIsRightButtonVisible(round(scrollLeft) < floor(scrollWidth - width) - 1)
  }

  const scrollTo = (left: number) => {
    containerRef?.current?.scrollTo({
      left,
      behavior: 'smooth',
    })
  }

  const scrollToLeft = () => {
    if (!containerRef?.current) {
      return
    }

    scrollTo(containerRef.current.scrollLeft - MOVE)
  }

  const scrollToRight = () => {
    if (!containerRef?.current) {
      return
    }

    scrollTo(containerRef.current.scrollLeft + MOVE)
  }

  const renderLeftBtn = () => {
    return (
      <div
        className={styles.scrollButtonLeft}
        style={
          {
            '--button-position-y': `${buttonPositionY}%`,
            '--button-transform-y': `-${buttonPositionY}%`,
          } as React.CSSProperties
        }
      >
        {isCircleType ? (
          <ButtonCircle.Left visible={isLeftButtonVisible} onClick={scrollToLeft} />
        ) : (
          <ButtonSlideScrollLeft visible={isLeftButtonVisible} onClick={scrollToLeft} />
        )}
      </div>
    )
  }

  const renderRightBtn = () => {
    return (
      <div
        className={styles.scrollButtonRight}
        style={
          {
            '--button-position-y': `${buttonPositionY}%`,
            '--button-transform-y': `-${buttonPositionY}%`,
          } as React.CSSProperties
        }
      >
        {isCircleType ? (
          <ButtonCircle.Right visible={isRightButtonVisible} onClick={scrollToRight} />
        ) : (
          <ButtonSlideScrollRight visible={isRightButtonVisible} onClick={scrollToRight} />
        )}
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div ref={containerRef} onScroll={displayButtons} className={styles.scrollContainer}>
        {renderLeftBtn()}
        {children}
        {!hideRightButton && renderRightBtn()}
      </div>
    </div>
  )
}
